import { useState } from 'react';
import { LandInspection as InspectionReport, MediaItem } from '@property/shared';
import { ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SafeImage } from '@/components/ui/safe-image';

interface OwnershipCardProps {
  report: InspectionReport;
  onMediaSelect: (item: MediaItem) => void;
}

export function OwnershipCard({ report, onMediaSelect }: OwnershipCardProps) {
  const [failedMediaIds, setFailedMediaIds] = useState<Set<string>>(new Set());

  const handleImageError = (id: string) => {
    setFailedMediaIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const hasNotice = report.ownershipNotice.value === 'Yes';
  const ownershipConflict =
    hasNotice ||
    (!!report.detectedOwnerName && report.detectedOwnerName !== report.registeredOwnerName);

  return (
    <div
      className={cn(
        'relative border border-dashed bg-background p-1.5',
        ownershipConflict ? 'border-ring/60' : 'border-sidebar-primary/50',
      )}
    >
      <div className="border border-border-strong/70 bg-card p-6 shadow-[0_12px_28px_-22px_rgba(29,43,34,0.5)] sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
            Ownership &amp; Boundary Status
          </h2>
          <span
            className={cn(
              'rotate-[-3deg] rounded-sm border-2 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em]',
              ownershipConflict
                ? 'border-ring text-ring'
                : 'border-sidebar-primary text-sidebar-primary',
            )}
          >
            {ownershipConflict ? 'Conflict found' : 'Title intact'}
          </span>
        </div>

        <div className="mt-6 flex flex-col items-start gap-8 md:flex-row">
          <div className="flex-1 space-y-5">
            {ownershipConflict ? (
              <>
                <div className="space-y-1">
                  <h3 className="font-display text-xl font-medium tracking-tight text-ring">
                    Owner Identity Conflict
                  </h3>
                  <p className="text-sm leading-relaxed text-foreground/65">
                    Field verification identified a potential ownership dispute or unauthorized
                    claim on the asset.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 pt-1 sm:grid-cols-2">
                  <div className="border border-border bg-background p-3.5">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/45">
                      Registered owner
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      {report.registeredOwnerName}
                    </p>
                  </div>
                  <div className="border border-ring/40 bg-ring/5 p-3.5">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-ring/70">
                      Detected on site
                    </p>
                    <p className="text-sm font-semibold text-ring">
                      {report.detectedOwnerName || 'Unauthorized Party'}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1">
                  <h3 className="font-display text-xl font-medium tracking-tight text-foreground">
                    Registered Ownership Intact
                  </h3>
                  <p className="text-sm leading-relaxed text-foreground/65">
                    The asset remains securely registered with no signs of external legal claims or
                    conflicting possession.
                  </p>
                </div>
                <div className="w-fit min-w-[200px] border border-sidebar-primary/30 bg-sidebar-primary/5 p-3.5">
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-sidebar-primary/70">
                    Verified owner
                  </p>
                  <p className="text-sm font-semibold text-sidebar-primary">
                    {report.registeredOwnerName}
                  </p>
                </div>
                <div className="flex w-fit items-center gap-2 text-sidebar-primary">
                  <ShieldCheck className="size-4" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/55">
                    Title records match site state
                  </span>
                </div>
              </>
            )}
          </div>

          {hasNotice && report.ownershipNotice.media && report.ownershipNotice.media.length > 0 && (
            <div className="w-full shrink-0 space-y-3 md:w-80">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/55">
                Conflict documentation
              </p>
              <div className="grid grid-cols-1 gap-3">
                {report.ownershipNotice.media.map((item: MediaItem, idx: number) => {
                  const broken = failedMediaIds.has(item.id);
                  return (
                    <div
                      key={idx}
                      onClick={() => !broken && onMediaSelect(item)}
                      className={cn(
                        'group relative aspect-video overflow-hidden rounded-sm border border-border bg-background',
                        !broken && 'cursor-zoom-in',
                      )}
                    >
                      {item.type === 'video' ? (
                        <video
                          src={item.url || ''}
                          className={cn(
                            'absolute inset-0 h-full w-full object-cover transition-all duration-500',
                            !broken && 'grayscale-[0.3] group-hover:grayscale-0',
                            broken && 'opacity-50',
                          )}
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <SafeImage
                          src={item.url || ''}
                          alt={item.label || ''}
                          className={cn(
                            'absolute inset-0 h-full w-full object-cover transition-all duration-500',
                            !broken && 'grayscale-[0.3] group-hover:grayscale-0',
                            broken && 'opacity-50',
                          )}
                          onErrorStateChange={(err) => err && handleImageError(item.id)}
                        />
                      )}
                      {!broken && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80" />
                      )}
                      {!broken && (
                        // eslint-disable-next-line local/no-raw-colors -- text on image overlay
                        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
                          <div className="space-y-0.5">
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">
                              {item.type}
                            </p>
                            <p className="text-xs font-semibold tracking-tight">{item.label}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
